/**
 * LearnPage — the deeper read on the dimensions Katha Mithra tags stories by.
 *
 * Sibling to the inline DimensionInfoButton popovers (which give a quick
 * blurb mid-task). This page is for readers who want to understand the
 * full frame: where the categories come from, why they're useful, and
 * what each value within them actually represents.
 *
 * Each section is anchored by dimension key (e.g. /learn#guna_arc) so
 * popovers can deep-link "Read the full thinking →" to the right spot.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader, { ToolbarCaptionButton } from '@/components/PageHeader';
import { useTagOptions } from '@/hooks/useTagOptions';
import { FILTER_DIMENSIONS } from '@/lib/filters';
import { DIMENSION_EDU } from '@/lib/dimensionEducation';
import Seo from '@/components/Seo';

// Human labels for the practice category_id buckets — used to subhead
// the practices section so 50+ entries stay scannable.
const PRACTICE_CATEGORY_LABELS = {
  lifestyle:        'Lifestyle',
  breath_pranayama: 'Breath & pranayama',
  study_wisdom:     'Study & wisdom',
  hatha_yoga:       'Hatha yoga',
  yogic_kriya:      'Yogic kriya',
  meditation:       'Meditation',
  chant_devotional: 'Chant / devotional',
  devotion:         'Devotion',
  service_karma:    'Service',
  classical_arts:   'Classical arts',
  therapeutic:      'Therapeutic',
  movement_other:   'Movement (other)',
};

// Render order for practice category buckets — keeps Isha-rooted and
// dharmic categories on top, then secular/modern ones.
const PRACTICE_CATEGORY_ORDER = [
  'meditation',
  'yogic_kriya',
  'hatha_yoga',
  'breath_pranayama',
  'chant_devotional',
  'devotion',
  'service_karma',
  'classical_arts',
  'study_wisdom',
  'lifestyle',
  'therapeutic',
  'movement_other',
];

const groupByCategory = (tags) => {
  const buckets = {};
  for (const t of tags) {
    const cat = t.category_id || 'other';
    if (!buckets[cat]) buckets[cat] = [];
    buckets[cat].push(t);
  }
  return PRACTICE_CATEGORY_ORDER
    .filter((cat) => buckets[cat]?.length)
    .map((cat) => ({ cat, label: PRACTICE_CATEGORY_LABELS[cat] || cat, items: buckets[cat] }))
    .concat(
      Object.keys(buckets)
        .filter((c) => !PRACTICE_CATEGORY_ORDER.includes(c))
        .map((c) => ({ cat: c, label: PRACTICE_CATEGORY_LABELS[c] || c, items: buckets[c] })),
    );
};

const TagRow = ({ tag, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-left w-full rounded-lg border border-[#E6E2D6] hover:border-[#C05621] bg-white hover:bg-[#FBF4EB] transition-colors p-3"
    data-testid={testId}
  >
    <div className="flex items-baseline gap-2">
      {tag.emoji ? <span className="shrink-0">{tag.emoji}</span> : null}
      <span className="font-medium text-[#2D2A26]">{tag.display_name}</span>
      {tag.sanskrit_name ? (
        <span className="text-xs uppercase tracking-wider text-[#6B665E] truncate">
          {tag.sanskrit_name}
        </span>
      ) : null}
    </div>
    {tag.description ? (
      <p className="mt-1 text-sm text-[#6B665E] leading-snug">{tag.description}</p>
    ) : null}
  </button>
);

const LearnPage = () => {
  const navigate = useNavigate();
  const { tagOptions } = useTagOptions();

  // Smooth-scroll to the right section when arriving with /learn#<dim>.
  // Run after first paint so anchored layout is settled.
  useEffect(() => {
    const hash = window.location.hash?.replace(/^#/, '');
    if (!hash) return;
    const el = document.getElementById(hash);
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const goToFilter = (param, tagId) => {
    navigate(`/search?${param}=${encodeURIComponent(tagId)}`);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo
        title="Learn the dimensions · Katha Mithra"
        description="The deeper read on how Katha Mithra tags stories: life areas, inner knots, obstacles, mental states, inner qualities, and the guna arc — where each category comes from and what its values mean."
        path="/learn"
      />
      <PageHeader>
        <ToolbarCaptionButton
          label="Back to Home"
          shortLabel="Home"
          onClick={() => navigate('/')}
          data-testid="learn-back-btn"
        />
      </PageHeader>

      <article
        className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
        data-testid="learn-page"
      >
        <header className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-serif tracking-tight leading-tight text-[#2D2A26] mb-4">
            How we tag stories
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-[#6B665E] max-w-2xl mx-auto">
            Katha Mithra organizes stories by ancient frames the Indic
            traditions developed for inner life. Not because the language is
            old, but because it's still the most precise vocabulary anyone
            has put together for what actually happens inside us.
          </p>
        </header>

        <nav
          className="mb-12 bg-white rounded-xl border border-[#E6E2D6] p-5"
          aria-label="Dimension index"
        >
          <div className="text-xs uppercase tracking-wider text-[#6B665E] mb-3">
            On this page
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-[#2D2A26]">
            {FILTER_DIMENSIONS.map(({ key }) => {
              const e = DIMENSION_EDU[key];
              if (!e) return null;
              return (
                <li key={key}>
                  <a
                    href={`#${key}`}
                    className="text-[#C05621] hover:text-[#A0461A] hover:underline underline-offset-2"
                  >
                    {e.title}
                  </a>
                  <span className="text-[#6B665E]"> — {e.sanskrit?.split(' — ')[0] || ''}</span>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="space-y-16">
          {FILTER_DIMENSIONS.map(({ key, param }) => {
            const e = DIMENSION_EDU[key];
            if (!e) return null;
            const options = tagOptions[key] || [];
            const isPractice = key === 'practice_ids';
            const grouped = isPractice ? groupByCategory(options) : null;

            return (
              <section
                key={key}
                id={key}
                className="scroll-mt-24"
                data-testid={`learn-section-${key}`}
              >
                <header className="mb-5">
                  <h2 className="text-3xl md:text-4xl font-serif text-[#2D2A26] leading-tight">
                    {e.title}
                  </h2>
                  {e.sanskrit && (
                    <div className="text-xs uppercase tracking-wider text-[#6B665E] mt-2">
                      {e.sanskrit}
                    </div>
                  )}
                </header>

                <div className="space-y-4 text-[#2D2A26] leading-relaxed text-base md:text-lg">
                  {(e.paragraphs || [e.summary]).map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>

                {options.length > 0 && (
                  <div className="mt-8">
                    <div className="text-xs uppercase tracking-wider text-[#6B665E] mb-3">
                      What each one means
                    </div>
                    {isPractice ? (
                      <div className="space-y-6">
                        {grouped.map(({ cat, label, items }) => (
                          <div key={cat}>
                            <div className="text-sm font-semibold text-[#2D2A26] mb-2">
                              {label}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {items.map((tag) => (
                                <TagRow
                                  key={`${key}-${tag.id}`}
                                  tag={tag}
                                  onClick={() => goToFilter(param, tag.id)}
                                  testId={`learn-tag-${key}-${tag.id}`}
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {options.map((tag) => (
                          <TagRow
                            key={`${key}-${tag.id}`}
                            tag={tag}
                            onClick={() => goToFilter(param, tag.id)}
                            testId={`learn-tag-${key}-${tag.id}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {e.how_we_use && (
                  <p className="mt-6 text-sm text-[#6B665E] italic border-l-2 border-[#C05621] pl-3">
                    {e.how_we_use}
                  </p>
                )}
              </section>
            );
          })}
        </div>

        <div className="mt-16 pt-8 border-t border-[#E6E2D6] text-center">
          <p className="text-[#6B665E] text-sm">
            Have a story to share?{' '}
            <button
              type="button"
              onClick={() => navigate('/submit')}
              className="text-[#C05621] hover:text-[#A0461A] font-medium hover:underline"
              data-testid="learn-cta-submit"
            >
              Start writing →
            </button>
          </p>
        </div>
      </article>
    </div>
  );
};

export default LearnPage;
